import React, { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import searchService, { SearchResult } from '../services/searchService';

export default function SearchTest() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchStats, setSearchStats] = useState<any>(null);

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Testing search for:', query);
      
      const response = await searchService.searchBusinesses({
        query: query.trim(),
        limit: 10
      });

      console.log('📊 Search response:', response);
      
      setResults(response.results);
      setSearchStats({
        totalCount: response.totalCount,
        hasExactMatches: response.hasExactMatches,
        hasFuzzyMatches: response.hasFuzzyMatches,
        searchTime: response.searchTime
      });

    } catch (err: any) {
      console.error('❌ Search test error:', err);
      setError(err.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-center">🔍 Search Test Component</h2>
      
      {/* Search Input */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Test search (try: IT, computer, plumber, etc.)"
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </>
          ) : (
            'Test Search'
          )}
        </button>
      </div>

      {/* Search Stats */}
      {searchStats && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">Search Statistics:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium">Total Results:</span>
              <span className="ml-2">{searchStats.totalCount}</span>
            </div>
            <div>
              <span className="font-medium">Search Time:</span>
              <span className="ml-2">{searchStats.searchTime.toFixed(2)}ms</span>
            </div>
            <div>
              <span className="font-medium">Exact Matches:</span>
              <span className={`ml-2 ${searchStats.hasExactMatches ? 'text-green-600' : 'text-red-600'}`}>
                {searchStats.hasExactMatches ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
              <span className="font-medium">Fuzzy Matches:</span>
              <span className={`ml-2 ${searchStats.hasFuzzyMatches ? 'text-orange-600' : 'text-gray-600'}`}>
                {searchStats.hasFuzzyMatches ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="font-semibold text-red-800 mb-2">Error:</h3>
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      {results.length > 0 ? (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Results ({results.length}):</h3>
          {results.map((result, index) => (
            <div key={result.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-blue-600">{result.store_name}</h4>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    result.business_type === 'trader' ? 'bg-blue-100 text-blue-800' :
                    result.business_type === 'craftie' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {result.business_type}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    result.match_type === 'exact_name' ? 'bg-green-100 text-green-800' :
                    result.match_type === 'keyword_match' ? 'bg-yellow-100 text-yellow-800' :
                    result.match_type === 'fuzzy_match' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {result.match_type}
                  </span>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-2">{result.location}</p>
              <p className="text-gray-700 text-sm mb-3">{result.description}</p>
              
              {result.keywords && result.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  <span className="text-xs text-gray-500">Keywords:</span>
                  {result.keywords.slice(0, 5).map((keyword, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                      {keyword}
                    </span>
                  ))}
                  {result.keywords.length > 5 && (
                    <span className="text-xs text-gray-500">+{result.keywords.length - 5} more</span>
                  )}
                </div>
              )}
              
              <div className="mt-2 text-xs text-gray-500">
                Relevance: {result.relevance_score.toFixed(2)} | 
                Similarity: {result.similarity_score.toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      ) : query && !loading && !error ? (
        <div className="text-center py-8 text-gray-500">
          <p>No results found for "{query}"</p>
          <p className="text-sm mt-2">Try different keywords or check if businesses are verified</p>
        </div>
      ) : null}

      {/* Instructions */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-blue-800 mb-2">Test Instructions:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Try searching for: "IT", "computer", "plumber", "repair"</li>
          <li>• Check console for detailed logs</li>
          <li>• Look for match types: exact_name, keyword_match, fuzzy_match</li>
          <li>• If no results, check if businesses are verified in database</li>
          <li>• If fuzzy search fails, fallback search will be used automatically</li>
        </ul>
      </div>
    </div>
  );
}
